import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { Input } from '@repo/ui/input';
import { useStep } from '@repo/ui/stepper';
import type { UseFormReturn } from 'react-hook-form';
import z from 'zod';

export const ZCreateOfficerInformationFormSchema = z.object({
  firstName: z.string().min(1, t('auth.firstNameIsRequired')),
  middleName: z.string().optional(),
  lastName: z.string().min(1, t('auth.lastNameIsRequired')),
  phone: z.string().optional(),
  email: z.email(t('extra.invalidEmailAddress')).min(1, t('extra.emailIsRequired')),
});
type TCreateOfficerInformationForm = z.infer<typeof ZCreateOfficerInformationFormSchema>;

type Props = {
  form: UseFormReturn<TCreateOfficerInformationForm>;
  onNext: () => void;
};

export const CreateOfficerInformationForm = ({ form, onNext }: Props) => {
  useLocale();

  const { previousStep } = useStep();

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(() => onNext())}>
        <fieldset className="space-y-4" disabled={form.formState.isSubmitting}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('reports.firstName2')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('reports.firstName2')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('reports.lastName2')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('reports.lastName2')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="middleName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('auth.middleName')}{' '}
                    <span className="font-normal text-muted-foreground">{t('auth.optional')}</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder={t('auth.middleName')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('reports.phone')}{' '}
                  <span className="font-normal text-muted-foreground">{t('auth.optional')}</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder={t('auth.phoneNumber')} type="tel" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.emailAddress')}</FormLabel>
                <FormControl>
                  <Input placeholder="officer@example.com" type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </fieldset>
        <div className="flex justify-between pt-4">
          <Button onClick={() => previousStep()} type="button" variant="outline">
            {t('auth.back')}{' '}
          </Button>
          <Button type="submit">{t('auth.continue')}</Button>
        </div>
      </form>
    </Form>
  );
};
